/**
 * LiteLLM Endpoints Management Component
 *
 * Provides a comprehensive interface for managing LiteLLM endpoints:
 * - List all endpoints with search, filtering by type, and pagination
 * - Create new endpoints
 * - Edit existing endpoints
 * - Toggle endpoint active status
 * - Delete endpoints (with confirmation)
 *
 * Access is controlled via RBAC:
 * - GLOBAL_ADMIN: Full access to all endpoints, can create/delete global endpoints
 * - TENANT_ADMIN: Manage tenant-specific endpoints, read global endpoints
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';

// =============================================================================
// Types
// =============================================================================

/**
 * Endpoint type enum
 */
type EndpointType = 'LLM' | 'STT' | 'TTS' | 'IMAGE' | 'EMBEDDING';

/**
 * LiteLLM Endpoint
 */
interface LiteLLMEndpoint {
  id: string;
  name: string;
  type: EndpointType;
  modelName: string;
  backend: string;
  config: Record<string, unknown>;
  isGlobal: boolean;
  tenantId: string | null;
  active: boolean;
  priority: number;
  inputCostPer1k: number | null;
  outputCostPer1k: number | null;
  searchEnabled: boolean;
  supportsToolCalls: boolean | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * API response for endpoint list
 */
interface EndpointsResponse {
  endpoints: LiteLLMEndpoint[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

/**
 * API response for backends list
 */
interface BackendsResponse {
  backends: string[];
}

interface GpuStackModelsResponse {
  apiBase: string;
  models: Array<{
    id: string;
    ownedBy: string | null;
    created: number | null;
  }>;
}

/**
 * Endpoint counts by type
 */
interface EndpointCounts {
  LLM: { total: number; active: number };
  STT: { total: number; active: number };
  TTS: { total: number; active: number };
  IMAGE: { total: number; active: number };
  EMBEDDING: { total: number; active: number };
}

/**
 * Form data for creating/editing endpoints
 */
interface EndpointFormData {
  id?: string;
  name: string;
  type: EndpointType;
  modelName: string;
  backend: string;
  config: Record<string, unknown>;
  isGlobal: boolean;
  tenantId: string | null;
  active: boolean;
  priority: number;
  inputCostPer1k?: number | null;
  outputCostPer1k?: number | null;
  searchEnabled: boolean;
  supportsToolCalls?: boolean | null;
}

/**
 * Modal state
 */
type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; endpoint: LiteLLMEndpoint }
  | { type: 'delete'; endpoint: LiteLLMEndpoint };

// =============================================================================
// Constants
// =============================================================================

const ENDPOINT_TYPES: EndpointType[] = ['LLM', 'STT', 'TTS', 'IMAGE', 'EMBEDDING'];

const ENDPOINT_TYPE_LABELS: Record<EndpointType, { label: string; description: string }> = {
  LLM: { label: 'Language Model', description: 'Chat & text generation' },
  STT: { label: 'Speech-to-Text', description: 'Audio transcription' },
  TTS: { label: 'Text-to-Speech', description: 'Audio generation' },
  IMAGE: { label: 'Image Generation', description: 'Image synthesis' },
  EMBEDDING: { label: 'Embeddings', description: 'Vector embeddings' },
};

const STATUS_COLORS = {
  active: { bg: 'bg-green-900/20', text: 'text-green-400', dot: 'bg-green-400' },
  inactive: { bg: 'bg-gray-900/20', text: 'text-gray-400', dot: 'bg-gray-400' },
} as const;

const TYPE_COLORS: Record<EndpointType, { bg: string; text: string }> = {
  LLM: { bg: 'bg-blue-900/30', text: 'text-blue-400' },
  STT: { bg: 'bg-purple-900/30', text: 'text-purple-400' },
  TTS: { bg: 'bg-pink-900/30', text: 'text-pink-400' },
  IMAGE: { bg: 'bg-orange-900/30', text: 'text-orange-400' },
  EMBEDDING: { bg: 'bg-cyan-900/30', text: 'text-cyan-400' },
};

const GPUSTACK_PROTOCOL_OPTIONS = [
  { value: 'http', label: 'http' },
  { value: 'https', label: 'https' },
];

function parseGpuStackConnection(config: Record<string, unknown> | undefined): {
  protocol: 'http' | 'https';
  host: string;
  port: string;
} {
  const configuredProtocol = config?.gpustack_protocol;
  const configuredHost = config?.gpustack_host;
  const configuredPort = config?.gpustack_port;

  const protocol = configuredProtocol === 'https' ? 'https' : 'http';
  const host = typeof configuredHost === 'string' ? configuredHost : '';
  const port = configuredPort != null ? String(configuredPort) : '';

  if (host && port) {
    return { protocol, host, port };
  }

  const apiBase = typeof config?.api_base === 'string' ? config.api_base : '';
  if (!apiBase) {
    return { protocol, host, port };
  }

  try {
    const parsed = new URL(apiBase);
    return {
      protocol: parsed.protocol === 'https:' ? 'https' : 'http',
      host: host || parsed.hostname,
      port: port || parsed.port || (parsed.protocol === 'https:' ? '443' : '80'),
    };
  } catch {
    return { protocol, host, port };
  }
}

function buildGpuStackApiBase(
  protocol: 'http' | 'https',
  host: string,
  port: string
): string {
  const url = new URL(`${protocol}://${host}`);
  url.port = port;
  url.pathname = '/v1';
  return url.toString().replace(/\/$/, '');
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Status badge component
 */
function StatusBadge({ active }: { active: boolean }) {
  const colors = active ? STATUS_COLORS.active : STATUS_COLORS.inactive;

  return (
    <div
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
      {active ? 'Active' : 'Inactive'}
    </div>
  );
}

/**
 * Endpoint type badge component
 */
function TypeBadge({ type }: { type: EndpointType }) {
  const colors = TYPE_COLORS[type];

  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}
    >
      {type}
    </span>
  );
}

/**
 * Global badge component
 */
function GlobalBadge({ isGlobal }: { isGlobal: boolean }) {
  if (!isGlobal) return null;

  return (
    <span className="inline-flex items-center rounded-md bg-yellow-900/30 px-2 py-1 text-xs font-medium text-yellow-400">
      Global
    </span>
  );
}

/**
 * Endpoint card component
 */
interface EndpointCardProps {
  endpoint: LiteLLMEndpoint;
  onEdit: (endpoint: LiteLLMEndpoint) => void;
  onToggleActive: (endpoint: LiteLLMEndpoint) => void;
  onDelete: (endpoint: LiteLLMEndpoint) => void;
  onTest: (endpoint: LiteLLMEndpoint) => void;
  isLoading: boolean;
  isTesting: boolean;
  canModify: boolean;
}

function EndpointCard({
  endpoint,
  onEdit,
  onToggleActive,
  onDelete,
  onTest,
  isLoading,
  isTesting,
  canModify,
}: EndpointCardProps) {
  return (
    <div className="rounded-lg bg-discord-darker border border-discord-light p-4 transition-all hover:border-discord-blurple hover:shadow-lg">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <TypeBadge type={endpoint.type} />
            <GlobalBadge isGlobal={endpoint.isGlobal} />
          </div>
          <button
            onClick={() => onEdit(endpoint)}
            className="mt-2 font-semibold text-white hover:text-discord-blurple transition-colors truncate block text-left"
          >
            {endpoint.name}
          </button>
          <p className="mt-1 text-sm text-gray-400 font-mono truncate">{endpoint.modelName}</p>
        </div>
        <StatusBadge active={endpoint.active} />
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs text-gray-500 flex-wrap">
        <span>Backend: {endpoint.backend}</span>
        <span>Priority: {endpoint.priority}</span>
        {(endpoint.inputCostPer1k != null || endpoint.outputCostPer1k != null) && (
          <span className="text-green-400">
            Cost: ${endpoint.inputCostPer1k ?? '?'}/${endpoint.outputCostPer1k ?? '?'} per 1K
          </span>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          Updated: {new Date(endpoint.updatedAt).toLocaleDateString()}
        </span>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onTest(endpoint)}
            disabled={isLoading || isTesting}
            className="rounded-md bg-blue-600/20 px-2.5 py-1 text-xs font-medium text-blue-400 hover:bg-blue-600/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Test endpoint connectivity"
          >
            {isTesting ? (
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </button>
          {canModify && (
            <>
              <button
                onClick={() => onToggleActive(endpoint)}
                disabled={isLoading}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  endpoint.active
                    ? 'bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30'
                    : 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                }`}
                title={endpoint.active ? 'Deactivate endpoint' : 'Activate endpoint'}
              >
                {endpoint.active ? 'Deactivate' : 'Activate'}
              </button>
              <button
                onClick={() => onEdit(endpoint)}
                disabled={isLoading}
                className="rounded-md bg-discord-light px-2.5 py-1 text-xs font-medium text-gray-300 hover:bg-discord-blurple hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Edit endpoint"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
              <button
                onClick={() => onDelete(endpoint)}
                disabled={isLoading}
                className="rounded-md bg-red-600/20 px-2.5 py-1 text-xs font-medium text-red-400 hover:bg-red-600/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete endpoint"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Endpoint row component for table view
 */
interface EndpointRowProps {
  endpoint: LiteLLMEndpoint;
  onEdit: (endpoint: LiteLLMEndpoint) => void;
  onToggleActive: (endpoint: LiteLLMEndpoint) => void;
  onDelete: (endpoint: LiteLLMEndpoint) => void;
  onTest: (endpoint: LiteLLMEndpoint) => void;
  isLoading: boolean;
  isTesting: boolean;
  canModify: boolean;
}

function EndpointRow({
  endpoint,
  onEdit,
  onToggleActive,
  onDelete,
  onTest,
  isLoading,
  isTesting,
  canModify,
}: EndpointRowProps) {
  return (
    <tr className="border-b border-discord-light hover:bg-discord-light/30 transition-colors">
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <button
            onClick={() => onEdit(endpoint)}
            className="font-medium text-white hover:text-discord-blurple transition-colors text-left"
          >
            {endpoint.name}
          </button>
          <span className="text-sm text-gray-500 font-mono">{endpoint.modelName}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <TypeBadge type={endpoint.type} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-400">
        {endpoint.backend}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <StatusBadge active={endpoint.active} />
          <GlobalBadge isGlobal={endpoint.isGlobal} />
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-400">
        {endpoint.priority}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onTest(endpoint)}
            disabled={isLoading || isTesting}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Test endpoint"
          >
            {isTesting ? 'Testing...' : 'Test'}
          </button>
          {canModify && (
            <>
              <button
                onClick={() => onToggleActive(endpoint)}
                disabled={isLoading}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  endpoint.active
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {endpoint.active ? 'Deactivate' : 'Activate'}
              </button>
              <button
                onClick={() => onEdit(endpoint)}
                disabled={isLoading}
                className="rounded-md bg-discord-light px-3 py-1.5 text-xs font-medium text-gray-300 hover:bg-discord-blurple hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Edit
              </button>
              <button
                onClick={() => onDelete(endpoint)}
                disabled={isLoading}
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

/**
 * Type filter tabs component
 */
interface TypeFilterProps {
  value: EndpointType | 'all';
  onChange: (filter: EndpointType | 'all') => void;
  counts: EndpointCounts | null;
}

function TypeFilterTabs({ value, onChange, counts }: TypeFilterProps) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2">
      <button
        onClick={() => onChange('all')}
        className={`rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
          value === 'all'
            ? 'bg-discord-blurple text-white'
            : 'bg-discord-light text-gray-300 hover:text-white'
        }`}
      >
        All
        {counts && (
          <span
            className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs ${
              value === 'all' ? 'bg-white/20' : 'bg-discord-darker'
            }`}
          >
            {Object.values(counts).reduce((sum, c) => sum + c.total, 0)}
          </span>
        )}
      </button>
      {ENDPOINT_TYPES.map((type) => (
        <button
          key={type}
          onClick={() => onChange(type)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
            value === type
              ? 'bg-discord-blurple text-white'
              : 'bg-discord-light text-gray-300 hover:text-white'
          }`}
        >
          {type}
          {counts && counts[type] && (
            <span
              className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs ${
                value === type ? 'bg-white/20' : 'bg-discord-darker'
              }`}
            >
              {counts[type].active}/{counts[type].total}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * Empty state component
 */
function EmptyState({
  filter,
  onCreateClick,
}: {
  filter: EndpointType | 'all';
  onCreateClick: () => void;
}) {
  const title = filter === 'all'
    ? 'No endpoints configured'
    : `No ${ENDPOINT_TYPE_LABELS[filter].label} endpoints`;

  const description = filter === 'all'
    ? 'Create your first LiteLLM endpoint to start routing AI requests'
    : `Add ${ENDPOINT_TYPE_LABELS[filter].description.toLowerCase()} endpoints`;

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <svg
        className="h-12 w-12 text-gray-500 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
        />
      </svg>
      <h3 className="text-lg font-medium text-white">{title}</h3>
      <p className="mt-1 text-sm text-gray-400">{description}</p>
      <button
        onClick={onCreateClick}
        className="mt-4 inline-flex items-center rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
      >
        Create Endpoint
      </button>
    </div>
  );
}

/**
 * Loading skeleton component
 */
function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="flex items-center justify-between">
        <div className="h-8 w-48 bg-discord-light rounded" />
        <div className="h-8 w-32 bg-discord-light rounded" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="rounded-lg bg-discord-darker border border-discord-light p-4">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="h-5 w-16 bg-discord-light rounded" />
                <div className="h-5 w-32 bg-discord-light rounded" />
                <div className="h-4 w-48 bg-discord-light rounded" />
              </div>
              <div className="h-6 w-16 bg-discord-light rounded-full" />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="h-4 w-24 bg-discord-light rounded" />
              <div className="flex gap-2">
                <div className="h-8 w-16 bg-discord-light rounded" />
                <div className="h-8 w-8 bg-discord-light rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Modal component
 */
function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 transition-opacity"
          onClick={onClose}
        />
        <div className={`relative w-full ${sizeClasses[size]} rounded-lg bg-discord-dark border border-discord-light p-6 shadow-xl`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">{title}</h2>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Form input component
 */
interface FormInputProps {
  label: string;
  id: string;
  type?: 'text' | 'number' | 'textarea' | 'password';
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  helpText?: string;
  error?: string;
  min?: number;
  max?: number;
  showToggle?: boolean;
}

function FormInput({
  label,
  id,
  type = 'text',
  value,
  onChange,
  placeholder,
  required,
  disabled,
  helpText,
  error,
  min,
  max,
  showToggle,
}: FormInputProps) {
  const [showPassword, setShowPassword] = useState(false);

  const inputClasses = `w-full rounded-md border bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 ${
    error
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
      : 'border-discord-light focus:border-discord-blurple focus:ring-discord-blurple'
  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

  const effectiveType = type === 'password' && showPassword ? 'text' : type;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      {type === 'textarea' ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          rows={3}
          className={inputClasses}
        />
      ) : (
        <div className="relative">
          <input
            type={effectiveType}
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            min={min}
            max={max}
            className={`${inputClasses} ${type === 'password' && showToggle ? 'pr-10' : ''}`}
          />
          {type === 'password' && showToggle && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              {showPassword ? (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          )}
        </div>
      )}
      {helpText && !error && (
        <p className="mt-1 text-xs text-gray-500">{helpText}</p>
      )}
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

/**
 * Form select component
 */
interface FormSelectProps {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  disabled?: boolean;
  helpText?: string;
  error?: string;
}

function FormSelect({
  label,
  id,
  value,
  onChange,
  options,
  required,
  disabled,
  helpText,
  error,
}: FormSelectProps) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full rounded-md border bg-discord-darkest py-2 px-3 text-white focus:outline-none focus:ring-1 ${
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-discord-light focus:border-discord-blurple focus:ring-discord-blurple'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {helpText && !error && (
        <p className="mt-1 text-xs text-gray-500">{helpText}</p>
      )}
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

/**
 * Form toggle component
 */
interface FormToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function FormToggle({ label, description, checked, onChange, disabled }: FormToggleProps) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <span className="text-sm font-medium text-gray-300">{label}</span>
        {description && (
          <p className="text-xs text-gray-500">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-discord-blurple' : 'bg-discord-light'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

/**
 * Endpoint form component
 */
interface EndpointFormProps {
  endpoint?: LiteLLMEndpoint | null;
  backends: string[];
  isGlobalAdmin: boolean;
  onSubmit: (data: EndpointFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function EndpointForm({
  endpoint,
  backends,
  isGlobalAdmin,
  onSubmit,
  onCancel,
  isLoading,
}: EndpointFormProps) {
  const isEditMode = !!endpoint?.id;
  const endpointConfig = (endpoint?.config ?? {}) as Record<string, unknown>;
  const initialGpuStackConnection = parseGpuStackConnection(endpointConfig);

  // Form state
  const [name, setName] = useState(endpoint?.name || '');
  const [type, setType] = useState<EndpointType>(endpoint?.type || 'LLM');
  const [modelName, setModelName] = useState(endpoint?.modelName || '');
  const [backend, setBackend] = useState(endpoint?.backend || 'openai');
  const [apiBase, setApiBase] = useState(
    (endpointConfig.api_base as string) || ''
  );
  const [apiKey, setApiKey] = useState(
    (endpointConfig.api_key as string) || ''
  );
  const [gpuStackProtocol, setGpuStackProtocol] = useState<'http' | 'https'>(
    initialGpuStackConnection.protocol
  );
  const [gpuStackHost, setGpuStackHost] = useState(initialGpuStackConnection.host);
  const [gpuStackPort, setGpuStackPort] = useState(initialGpuStackConnection.port);
  const [gpuStackModels, setGpuStackModels] = useState<GpuStackModelsResponse['models']>([]);
  const [gpuStackError, setGpuStackError] = useState<string | null>(null);
  const [isLoadingGpuStackModels, setIsLoadingGpuStackModels] = useState(false);
  const [priority, setPriority] = useState(String(endpoint?.priority ?? 0));
  const [isGlobal, setIsGlobal] = useState(endpoint?.isGlobal ?? false);
  const [active, setActive] = useState(endpoint?.active ?? true);
  const [inputCostPer1k, setInputCostPer1k] = useState(
    endpoint?.inputCostPer1k != null ? String(endpoint.inputCostPer1k) : ''
  );
  const [outputCostPer1k, setOutputCostPer1k] = useState(
    endpoint?.outputCostPer1k != null ? String(endpoint.outputCostPer1k) : ''
  );
  const [searchEnabled, setSearchEnabled] = useState(endpoint?.searchEnabled ?? false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isGpuStackBackend = backend === 'gpustack';
  const computedGpuStackApiBase = isGpuStackBackend && gpuStackHost.trim() && gpuStackPort.trim()
    ? buildGpuStackApiBase(gpuStackProtocol, gpuStackHost.trim(), gpuStackPort.trim())
    : '';

  /**
   * Validate form
   */
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.length > 100) {
      newErrors.name = 'Name must be at most 100 characters';
    }

    if (!modelName.trim()) {
      newErrors.modelName = 'Model name is required';
    } else if (modelName.length > 100) {
      newErrors.modelName = 'Model name must be at most 100 characters';
    }

    if (!backend.trim()) {
      newErrors.backend = 'Backend is required';
    }

    if (isGpuStackBackend) {
      if (!gpuStackHost.trim()) {
        newErrors.gpuStackHost = 'GPUStack host or IP is required';
      }

      const gpuStackPortValue = parseInt(gpuStackPort, 10);
      if (gpuStackPort.trim() === '' || isNaN(gpuStackPortValue) || gpuStackPortValue < 1 || gpuStackPortValue > 65535) {
        newErrors.gpuStackPort = 'GPUStack port must be between 1 and 65535';
      }

      if (!apiKey.trim()) {
        newErrors.apiKey = 'GPUStack API key is required';
      }
    }

    const resolvedApiBase = isGpuStackBackend ? computedGpuStackApiBase : apiBase;
    if (resolvedApiBase && !/^https?:\/\/.+/.test(resolvedApiBase)) {
      newErrors.apiBase = 'Invalid URL format';
    }

    const priorityNum = parseInt(priority, 10);
    if (isNaN(priorityNum) || priorityNum < 0 || priorityNum > 1000) {
      newErrors.priority = 'Priority must be between 0 and 1000';
    }

    if (inputCostPer1k && (isNaN(parseFloat(inputCostPer1k)) || parseFloat(inputCostPer1k) < 0)) {
      newErrors.inputCostPer1k = 'Input cost must be a non-negative number';
    }

    if (outputCostPer1k && (isNaN(parseFloat(outputCostPer1k)) || parseFloat(outputCostPer1k) < 0)) {
      newErrors.outputCostPer1k = 'Output cost must be a non-negative number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [
    name,
    modelName,
    backend,
    apiBase,
    computedGpuStackApiBase,
    gpuStackHost,
    gpuStackPort,
    apiKey,
    isGpuStackBackend,
    priority,
    inputCostPer1k,
    outputCostPer1k,
  ]);

  const handleFetchGpuStackModels = useCallback(async () => {
    if (!gpuStackHost.trim() || !gpuStackPort.trim() || !apiKey.trim()) {
      setGpuStackError('GPUStack host, port, and API key are required before querying models.');
      return;
    }

    const parsedPort = parseInt(gpuStackPort, 10);
    if (isNaN(parsedPort) || parsedPort < 1 || parsedPort > 65535) {
      setGpuStackError('GPUStack port must be between 1 and 65535.');
      return;
    }

    setIsLoadingGpuStackModels(true);
    setGpuStackError(null);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.post<GpuStackModelsResponse>('/litellm/gpustack/models', {
        protocol: gpuStackProtocol,
        host: gpuStackHost.trim(),
        port: parsedPort,
        apiKey,
      });

      if (isSuccess(result)) {
        setGpuStackModels(result.data.models);
        setApiBase(result.data.apiBase);

        if (!modelName && result.data.models.length > 0) {
          setModelName(result.data.models[0]?.id ?? '');
        }
      } else {
        setGpuStackModels([]);
        setGpuStackError(result.error.message || 'Failed to query GPUStack models');
      }
    } catch {
      setGpuStackModels([]);
      setGpuStackError('Failed to query GPUStack models. Please try again.');
    } finally {
      setIsLoadingGpuStackModels(false);
    }
  }, [apiKey, gpuStackHost, gpuStackPort, gpuStackProtocol, modelName]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validate()) return;

      setIsSubmitting(true);
      setErrors({});

      const config: Record<string, unknown> = {};
      const resolvedApiBase = isGpuStackBackend ? computedGpuStackApiBase : apiBase.trim();
      if (resolvedApiBase) {
        config.api_base = resolvedApiBase;
      }
      if (apiKey) {
        config.api_key = apiKey.trim();
      }
      if (isGpuStackBackend) {
        config.gpustack_protocol = gpuStackProtocol;
        config.gpustack_host = gpuStackHost.trim();
        config.gpustack_port = parseInt(gpuStackPort, 10);
      }

      const data: EndpointFormData = {
        id: endpoint?.id,
        name: name.trim(),
        type,
        modelName: modelName.trim(),
        backend: backend.trim(),
        config,
        isGlobal: isGlobalAdmin ? isGlobal : false,
        tenantId: null,
        active,
        priority: parseInt(priority, 10),
        inputCostPer1k: inputCostPer1k ? parseFloat(inputCostPer1k) : null,
        outputCostPer1k: outputCostPer1k ? parseFloat(outputCostPer1k) : null,
        searchEnabled: type === 'LLM' ? searchEnabled : false,
      };

      try {
        onSubmit(data);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validate,
      name,
      type,
      modelName,
      backend,
      apiBase,
      apiKey,
      computedGpuStackApiBase,
      gpuStackProtocol,
      gpuStackHost,
      gpuStackPort,
      isGpuStackBackend,
      priority,
      isGlobal,
      active,
      inputCostPer1k,
      outputCostPer1k,
      searchEnabled,
      endpoint?.id,
      isGlobalAdmin,
      onSubmit,
    ]
  );

  const typeOptions = ENDPOINT_TYPES.map((t) => ({
    value: t,
    label: `${t} - ${ENDPOINT_TYPE_LABELS[t].description}`,
  }));

  const backendOptions = backends.map((b) => ({ value: b, label: b }));

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error Alert */}
      {errors.general && (
        <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
          <div className="flex items-start">
            <svg
              className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-red-200">{errors.general}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form Fields */}
      <div className="rounded-lg bg-discord-darker border border-discord-light p-6 space-y-4">
        <FormInput
          label="Endpoint Name"
          id="name"
          value={name}
          onChange={setName}
          placeholder="GPT-4o Mini"
          required
          disabled={isLoading || isSubmitting}
          error={errors.name}
          helpText="Display name for this endpoint"
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormSelect
            label="Endpoint Type"
            id="type"
            value={type}
            onChange={(v) => setType(v as EndpointType)}
            options={typeOptions}
            required
            disabled={isLoading || isSubmitting || isEditMode}
            helpText="Type of AI service"
          />

          <FormSelect
            label="Backend Provider"
            id="backend"
            value={backend}
            onChange={setBackend}
            options={backendOptions}
            required
            disabled={isLoading || isSubmitting}
            error={errors.backend}
            helpText="AI provider backend"
          />
        </div>

        <FormInput
          label="Model Name"
          id="modelName"
          value={modelName}
          onChange={setModelName}
          placeholder={isGpuStackBackend ? 'Select a GPUStack model or type one manually' : 'gpt-4o-mini'}
          required
          disabled={isLoading || isSubmitting}
          error={errors.modelName}
          helpText="Model identifier used in API calls"
        />

        {isGpuStackBackend ? (
          <div className="rounded-md border border-discord-light bg-discord-dark p-4 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-200">GPUStack Connection</h4>
                <p className="mt-1 text-xs text-gray-500">
                  Connect directly to a GPUStack OpenAI-compatible endpoint and query its published models.
                </p>
              </div>
              <button
                type="button"
                onClick={() => void handleFetchGpuStackModels()}
                disabled={isLoading || isSubmitting || isLoadingGpuStackModels}
                className="rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingGpuStackModels ? 'Querying...' : 'Query GPUStack Models'}
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <FormSelect
                label="Protocol"
                id="gpuStackProtocol"
                value={gpuStackProtocol}
                onChange={(value) => setGpuStackProtocol(value as 'http' | 'https')}
                options={GPUSTACK_PROTOCOL_OPTIONS}
                disabled={isLoading || isSubmitting || isLoadingGpuStackModels}
              />
              <FormInput
                label="Host / IP"
                id="gpuStackHost"
                value={gpuStackHost}
                onChange={setGpuStackHost}
                placeholder="68.14.228.6"
                disabled={isLoading || isSubmitting || isLoadingGpuStackModels}
                error={errors.gpuStackHost}
              />
              <FormInput
                label="Port"
                id="gpuStackPort"
                type="number"
                value={gpuStackPort}
                onChange={setGpuStackPort}
                placeholder="80"
                disabled={isLoading || isSubmitting || isLoadingGpuStackModels}
                error={errors.gpuStackPort}
              />
            </div>

            <FormInput
              label="GPUStack API Key"
              id="apiKey"
              type="password"
              value={apiKey}
              onChange={setApiKey}
              placeholder="gpustack_..."
              disabled={isLoading || isSubmitting || isLoadingGpuStackModels}
              error={errors.apiKey}
              helpText="Required for GPUStack model discovery and direct requests"
              showToggle
            />

            <div className="rounded-md bg-discord-darker border border-discord-light p-3">
              <p className="text-xs font-medium text-gray-300">Computed API Base</p>
              <p className="mt-1 break-all font-mono text-xs text-gray-400">
                {computedGpuStackApiBase || 'Enter host and port to build the GPUStack API base URL'}
              </p>
              {errors.apiBase && (
                <p className="mt-2 text-xs text-red-400">{errors.apiBase}</p>
              )}
              {gpuStackError && (
                <p className="mt-2 text-xs text-red-400">{gpuStackError}</p>
              )}
            </div>

            {gpuStackModels.length > 0 && (
              <FormSelect
                label="Available GPUStack Models"
                id="gpuStackModels"
                value={modelName}
                onChange={setModelName}
                options={gpuStackModels.map((model) => ({
                  value: model.id,
                  label: model.ownedBy ? `${model.id} (${model.ownedBy})` : model.id,
                }))}
                disabled={isLoading || isSubmitting || isLoadingGpuStackModels}
                helpText="Selecting a model fills the Model Name field above"
              />
            )}
          </div>
        ) : (
          <>
            <FormInput
              label="API Base URL"
              id="apiBase"
              value={apiBase}
              onChange={setApiBase}
              placeholder="https://api.openai.com/v1"
              disabled={isLoading || isSubmitting}
              error={errors.apiBase}
              helpText="Optional: Custom API base URL"
            />

            <FormInput
              label="API Key"
              id="apiKey"
              type="password"
              value={apiKey}
              onChange={setApiKey}
              placeholder="sk-..."
              disabled={isLoading || isSubmitting}
              helpText="Optional: API key for this endpoint (leave empty to use environment variable)"
              showToggle
            />
          </>
        )}

        <FormInput
          label="Priority"
          id="priority"
          type="number"
          value={priority}
          onChange={setPriority}
          min={0}
          max={1000}
          disabled={isLoading || isSubmitting}
          error={errors.priority}
          helpText="Higher priority endpoints are preferred (0-1000)"
        />

        {/* Cost Tracking Section */}
        <div className="pt-4 border-t border-discord-light">
          <h4 className="text-sm font-medium text-gray-300 mb-3">Cost Tracking (per 1K tokens)</h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormInput
              label="Input Cost"
              id="inputCostPer1k"
              type="number"
              value={inputCostPer1k}
              onChange={setInputCostPer1k}
              placeholder="0.00015"
              disabled={isLoading || isSubmitting}
              error={errors.inputCostPer1k}
              helpText="Cost per 1K input/prompt tokens (USD)"
            />
            <FormInput
              label="Output Cost"
              id="outputCostPer1k"
              type="number"
              value={outputCostPer1k}
              onChange={setOutputCostPer1k}
              placeholder="0.0006"
              disabled={isLoading || isSubmitting}
              error={errors.outputCostPer1k}
              helpText="Cost per 1K output/completion tokens (USD)"
            />
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Leave empty to use default pricing from the cost service
          </p>
        </div>

        <div className="space-y-3 pt-2 border-t border-discord-light">
          <FormToggle
            label="Active"
            description="Inactive endpoints are not available for use"
            checked={active}
            onChange={setActive}
            disabled={isLoading || isSubmitting}
          />

          {isGlobalAdmin && (
            <FormToggle
              label="Global Endpoint"
              description="Global endpoints are available to all tenants"
              checked={isGlobal}
              onChange={setIsGlobal}
              disabled={isLoading || isSubmitting}
            />
          )}

          {type === 'LLM' && (
            <FormToggle
              label="Enable Web Search (SearXNG)"
              description="Allow bots using this endpoint to search the web via tool calling"
              checked={searchEnabled}
              onChange={setSearchEnabled}
              disabled={isLoading || isSubmitting}
            />
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading || isSubmitting}
          className="rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting
            ? isEditMode
              ? 'Saving...'
              : 'Creating...'
            : isEditMode
              ? 'Save Changes'
              : 'Create Endpoint'}
        </button>
      </div>
    </form>
  );
}

/**
 * Delete confirmation modal content
 */
function DeleteConfirmation({
  endpoint,
  onConfirm,
  onCancel,
  isLoading,
}: {
  endpoint: LiteLLMEndpoint;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-red-900/30 border border-red-500/50 p-4">
        <div className="flex items-start">
          <svg
            className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-red-300">
              Delete endpoint?
            </h4>
            <p className="mt-1 text-sm text-red-200/80">
              This action cannot be undone. Bots using this endpoint will need
              to be reconfigured.
            </p>
          </div>
        </div>
      </div>

      <div className="text-sm text-gray-300">
        <p>
          You are about to delete <strong className="text-white">{endpoint.name}</strong>
        </p>
        <div className="mt-2 space-y-1 text-gray-400">
          <p>Type: {endpoint.type}</p>
          <p>Model: {endpoint.modelName}</p>
          <p>Backend: {endpoint.backend}</p>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isLoading}
          className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Deleting...' : 'Delete Endpoint'}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * LiteLLM Endpoints management page component
 *
 * Provides comprehensive endpoint management for admins:
 * - List endpoints with filtering by type and search
 * - Create, edit, toggle, and delete endpoints
 */
export function LiteLLMEndpoints() {
  useAuth(); // Ensure user is authenticated
  const { isGlobalAdmin } = useTenant();

  const [endpoints, setEndpoints] = useState<LiteLLMEndpoint[]>([]);
  const [backends, setBackends] = useState<string[]>([]);
  const [counts, setCounts] = useState<EndpointCounts | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [testingEndpointId, setTestingEndpointId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<EndpointType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    hasMore: false,
  });

  /**
   * Fetch endpoints from API
   */
  const fetchEndpoints = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: '20',
        sortBy: 'priority',
        sortOrder: 'desc',
      });

      if (searchQuery) {
        queryParams.set('search', searchQuery);
      }

      if (typeFilter !== 'all') {
        queryParams.set('type', typeFilter);
      }

      const result = await apiClient.get<EndpointsResponse>(`/litellm/endpoints?${queryParams}`);

      if (isSuccess(result)) {
        setEndpoints(result.data.endpoints);
        setPagination({
          total: result.data.pagination.total,
          totalPages: result.data.pagination.totalPages,
          hasMore: result.data.pagination.hasMore,
        });
      } else {
        setError(result.error.message || 'Failed to fetch endpoints');
      }
    } catch {
      setError('Failed to load endpoints. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [page, searchQuery, typeFilter]);

  /**
   * Fetch backends list
   */
  const fetchBackends = useCallback(async () => {
    const apiClient = getApiClient();

    try {
      const result = await apiClient.get<BackendsResponse>('/litellm/backends');

      if (isSuccess(result)) {
        setBackends(result.data.backends);
      }
    } catch {
      // Use default backends on error
      setBackends(['openai', 'anthropic', 'ollama', 'vllm', 'azure', 'gpustack']);
    }
  }, []);

  /**
   * Fetch endpoint counts
   */
  const fetchCounts = useCallback(async () => {
    const apiClient = getApiClient();

    try {
      const result = await apiClient.get<{ counts: EndpointCounts }>('/litellm/counts');

      if (isSuccess(result)) {
        setCounts(result.data.counts);
      }
    } catch {
      // Ignore count errors
    }
  }, []);

  /**
   * Toggle endpoint active status
   */
  const handleToggleActive = useCallback(async (endpoint: LiteLLMEndpoint) => {
    setIsActionLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.post<LiteLLMEndpoint>(`/litellm/endpoints/${endpoint.id}/toggle`);

      if (isSuccess(result)) {
        setEndpoints((prev) =>
          prev.map((e) =>
            e.id === endpoint.id ? { ...e, active: !endpoint.active } : e
          )
        );
        void fetchCounts();
      } else {
        setError(result.error.message || 'Failed to update endpoint status');
      }
    } catch {
      setError('Failed to update endpoint. Please try again.');
    } finally {
      setIsActionLoading(false);
    }
  }, [fetchCounts]);

  /**
   * Handle form submission (create/edit)
   */
  const handleFormSubmit = useCallback(
    async (data: EndpointFormData) => {
      setIsActionLoading(true);
      setError(null);

      const apiClient = getApiClient();

      try {
        let result;
        if (data.id) {
          result = await apiClient.put<LiteLLMEndpoint>(`/litellm/endpoints/${data.id}`, data);
        } else {
          result = await apiClient.post<LiteLLMEndpoint>('/litellm/endpoints', data);
        }

        if (isSuccess(result)) {
          if (data.id) {
            setEndpoints((prev) =>
              prev.map((e) =>
                e.id === data.id ? result.data : e
              )
            );
          } else {
            setEndpoints((prev) => [result.data, ...prev]);
          }
          setModal({ type: 'none' });
          void fetchCounts();
        } else {
          setError(result.error.message || 'Failed to save endpoint');
        }
      } catch {
        setError('Failed to save endpoint. Please try again.');
      } finally {
        setIsActionLoading(false);
      }
    },
    [fetchCounts]
  );

  /**
   * Delete endpoint
   */
  const handleDeleteConfirm = useCallback(async () => {
    if (modal.type !== 'delete') return;

    setIsActionLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.delete<{ id: string; deleted: boolean }>(
        `/litellm/endpoints/${modal.endpoint.id}`
      );

      if (isSuccess(result)) {
        setEndpoints((prev) => prev.filter((e) => e.id !== modal.endpoint.id));
        setModal({ type: 'none' });
        void fetchCounts();
      } else {
        setError(result.error.message || 'Failed to delete endpoint');
      }
    } catch {
      setError('Failed to delete endpoint. Please try again.');
    } finally {
      setIsActionLoading(false);
    }
  }, [modal, fetchCounts]);

  /**
   * Test endpoint connectivity
   */
  const handleTestEndpoint = useCallback(async (endpoint: LiteLLMEndpoint) => {
    setTestingEndpointId(endpoint.id);
    setError(null);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.post<{
        success: boolean;
        endpointId: string;
        endpointType: string;
        responseTimeMs: number;
        message: string;
        error?: string;
      }>(`/litellm/endpoints/${endpoint.id}/test`, {});

      if (isSuccess(result)) {
        const testData = result.data;
        if (testData.success) {
          // Show success message (using alert for simplicity - could be replaced with toast)
          alert(`✅ Test Passed!\n\n${testData.message}\nResponse time: ${testData.responseTimeMs}ms`);
        } else {
          alert(`❌ Test Failed!\n\n${testData.error || 'Unknown error'}\nResponse time: ${testData.responseTimeMs}ms`);
        }
      } else {
        setError(result.error.message || 'Failed to test endpoint');
      }
    } catch {
      setError('Failed to test endpoint. Please try again.');
    } finally {
      setTestingEndpointId(null);
    }
  }, []);

  /**
   * Initial data fetch
   */
  useEffect(() => {
    void fetchEndpoints();
    void fetchBackends();
    void fetchCounts();
  }, [fetchEndpoints, fetchBackends, fetchCounts]);

  /**
   * Filter endpoints by search (client-side for current page)
   */
  const filteredEndpoints = useMemo(() => {
    if (!searchQuery.trim()) return endpoints;

    const query = searchQuery.toLowerCase();
    return endpoints.filter(
      (endpoint) =>
        endpoint.name.toLowerCase().includes(query) ||
        endpoint.modelName.toLowerCase().includes(query) ||
        endpoint.backend.toLowerCase().includes(query)
    );
  }, [endpoints, searchQuery]);

  /**
   * Check if user can modify an endpoint
   */
  const canModifyEndpoint = useCallback(
    (endpoint: LiteLLMEndpoint): boolean => {
      // Global admin can modify everything
      if (isGlobalAdmin) return true;
      // Tenant admins can only modify non-global endpoints
      return !endpoint.isGlobal;
    },
    [isGlobalAdmin]
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">LiteLLM Endpoints</h1>
            <p className="mt-1 text-gray-400">
              Manage AI service endpoints for LLM, TTS, STT, and image generation
            </p>
          </div>
          <button
            onClick={() => setModal({ type: 'create' })}
            className="inline-flex items-center gap-2 rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Endpoint
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
            <div className="flex items-start">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-200">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-3 text-sm font-medium text-red-400 hover:text-red-300"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Type Filter Tabs */}
            <TypeFilterTabs
              value={typeFilter}
              onChange={setTypeFilter}
              counts={counts}
            />

            {/* Filters and Controls */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                {/* Search Input */}
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search endpoints..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-64 rounded-md border border-discord-light bg-discord-darker py-2 pl-10 pr-4 text-sm text-white placeholder-gray-500 focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* View Toggle */}
                <div className="flex rounded-md bg-discord-light">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`rounded-l-md px-3 py-2 text-sm transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-discord-blurple text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    title="Grid view"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`rounded-r-md px-3 py-2 text-sm transition-colors ${
                      viewMode === 'table'
                        ? 'bg-discord-blurple text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    title="Table view"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 10h16M4 14h16M4 18h16"
                      />
                    </svg>
                  </button>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={() => void fetchEndpoints()}
                  disabled={isLoading}
                  className="rounded-md bg-discord-light p-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <svg
                    className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Endpoint List */}
            {filteredEndpoints.length === 0 ? (
              <EmptyState
                filter={typeFilter}
                onCreateClick={() => setModal({ type: 'create' })}
              />
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredEndpoints.map((endpoint) => (
                  <EndpointCard
                    key={endpoint.id}
                    endpoint={endpoint}
                    onEdit={(e) => setModal({ type: 'edit', endpoint: e })}
                    onToggleActive={handleToggleActive}
                    onDelete={(e) => setModal({ type: 'delete', endpoint: e })}
                    onTest={handleTestEndpoint}
                    isLoading={isActionLoading}
                    isTesting={testingEndpointId === endpoint.id}
                    canModify={canModifyEndpoint(endpoint)}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-discord-light overflow-hidden overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead className="bg-discord-darker">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Name / Model
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Backend
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Priority
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-discord-dark">
                    {filteredEndpoints.map((endpoint) => (
                      <EndpointRow
                        key={endpoint.id}
                        endpoint={endpoint}
                        onEdit={(e) => setModal({ type: 'edit', endpoint: e })}
                        onToggleActive={handleToggleActive}
                        onDelete={(e) => setModal({ type: 'delete', endpoint: e })}
                        onTest={handleTestEndpoint}
                        isLoading={isActionLoading}
                        isTesting={testingEndpointId === endpoint.id}
                        canModify={canModifyEndpoint(endpoint)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">
                  Page {page} of {pagination.totalPages} ({pagination.total} endpoints)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md bg-discord-light px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!pagination.hasMore}
                    className="rounded-md bg-discord-light px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="flex items-center justify-between text-sm text-gray-400">
              <span>
                Showing {filteredEndpoints.length} of {endpoints.length} endpoints
              </span>
              {counts && (
                <span>
                  {Object.values(counts).reduce((sum, c) => sum + c.active, 0)} active ·{' '}
                  {Object.values(counts).reduce((sum, c) => sum + c.total - c.active, 0)} inactive
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modal.type === 'create' || modal.type === 'edit'}
        onClose={() => setModal({ type: 'none' })}
        title={modal.type === 'create' ? 'Create New Endpoint' : 'Edit Endpoint'}
        size="lg"
      >
        <EndpointForm
          endpoint={modal.type === 'edit' ? modal.endpoint : null}
          backends={backends}
          isGlobalAdmin={isGlobalAdmin}
          onSubmit={handleFormSubmit}
          onCancel={() => setModal({ type: 'none' })}
          isLoading={isActionLoading}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={modal.type === 'delete'}
        onClose={() => setModal({ type: 'none' })}
        title="Delete Endpoint"
      >
        {modal.type === 'delete' && (
          <DeleteConfirmation
            endpoint={modal.endpoint}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setModal({ type: 'none' })}
            isLoading={isActionLoading}
          />
        )}
      </Modal>
    </Layout>
  );
}

export default LiteLLMEndpoints;
